import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FaxinaFamilia, FaxinaItem } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import { faxinaFamilias, faxinaItens, marcarFaxina } from '../lib/consultas';
import {
  avisoDeParecido, familiaLegivel, familiaSemNada, resumoDasFontes, rotuloDoItem,
} from '../lib/faxina';
import { dataBR, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// =============================================================================
// A LISTA DE FAXINA DO CADASTRO (120 — 18/10/2026)
//
// "Agora faz, pois o foco primordial é fazer vídeos e cadastrar as peças."
// — o dono, 18/10/2026, revertendo o "não" de 27/08. MUTIRÃO continua não
// haverá (08/10): esta tela é a lista de trabalho peça a peça.
//
// POR QUE DOIS NÍVEIS. Medido em produção em 18/10: o balcão vendeu 2.520 vezes
// peça não cadastrada, em 2.102 textos DIFERENTES — uma lista por nome exato
// seria duas mil linhas de contagem 1, que não ordena trabalho nenhum. Por
// FAMÍLIA (a primeira palavra) as 10 maiores são 33,5% das linhas e as 20
// maiores 46,7%. Então: família primeiro, nome exato dentro dela.
//
// NENHUM VALOR EM DINHEIRO NESTA TELA. Ela é de gerente (podeGerenciarCatalogo),
// e o dono tirou o faturamento de quem não é ele (090, e 07/10: o gerente vê a
// LISTA, nunca a soma). A ordem é por NÚMERO DE VEZES, nunca por unidade — um
// pacote de 48 abraçadeiras vendido 16 vezes não pode pular à frente de uma
// lâmpada vendida 50.
//
// O QUE A TELA NÃO FAZ: contar. Toda conta vem do banco, porque a agregação do
// PostgREST está desligada de propósito (agregacaoDesligada.test.ts) e porque a
// leitura direta de sale_items devolveria ao gerente só as vendas DELE.
// =============================================================================

const DIAS = 90;

export default function Faxina() {
  const [familia, setFamilia] = useState<string | null>(null);

  const familias = useQuery({
    queryKey: ['faxina-familias', DIAS],
    queryFn: () => faxinaFamilias(DIAS),
  });

  return (
    <Layout titulo="O que falta cadastrar">
      <div className="space-y-4">
        <p className="text-sm text-mute">
          O que o balcão vendeu, encomendou ou orçou <strong>sem ter no catálogo</strong>, nos
          últimos {DIAS} dias. O mais pedido primeiro. Toque em “Cadastrar” e a ficha abre com o
          nome, o código e o carro já preenchidos.
        </p>

        {familias.isPending && <Carregando texto="Lendo o que o balcão pediu" />}
        {familias.error && <Aviso tipo="erro">{mensagemDeErro(familias.error)}</Aviso>}

        {familias.data && familias.data.length === 0 && (
          <Aviso tipo="ok">
            Nada pendente: tudo o que saiu nos últimos {DIAS} dias já está no catálogo ou já foi
            decidido.
          </Aviso>
        )}

        {familias.data && familias.data.length > 0 && (
          <>
            <Familias lista={familias.data} escolhida={familia} aoEscolher={setFamilia} />
            <Itens familia={familia} />
          </>
        )}
      </div>
    </Layout>
  );
}

// --- Primeiro nível: as famílias ---------------------------------------------

function Familias({ lista, escolhida, aoEscolher }: {
  lista: ReadonlyArray<FaxinaFamilia>;
  escolhida: string | null;
  aoEscolher: (f: string | null) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {/* "As mais pedidas" é a lista SEM filtro de família: é ela que resolve
            o começo do trabalho — ~20 cadastros cobrem ~250 vendas. */}
        <button type="button"
                className={`min-h-11 px-3 text-sm ${escolhida === null ? 'botao-terciario border-mute bg-edge' : 'botao-terciario'}`}
                onClick={() => aoEscolher(null)}>
          As mais pedidas
        </button>
        {lista.map((f) => (
          <button key={f.familia} type="button"
                  className={`min-h-11 px-3 text-sm ${escolhida === f.familia ? 'botao-terciario border-mute bg-edge' : 'botao-terciario'}`}
                  onClick={() => aoEscolher(f.familia)}
                  aria-label={`${familiaLegivel(f.familia)}: ${f.vezes} pedidos, ${f.ja_no_catalogo} cadastradas`}>
            {familiaLegivel(f.familia)} <span className="text-mute">· {f.vezes}</span>
            {/* A família sem NADA cadastrado é o primeiro trabalho: em 18/10
                eram 77% das linhas (palheta, filtro, maçaneta, correia…). */}
            {familiaSemNada(f) && <span className="ml-1 text-alerta" aria-hidden="true">•</span>}
          </button>
        ))}
      </div>
      <p className="text-xs text-mute">
        <span className="text-alerta">•</span> família sem nenhuma peça cadastrada.
      </p>
    </div>
  );
}

// --- Segundo nível: os nomes exatos ------------------------------------------

function Itens({ familia }: { familia: string | null }) {
  const itens = useQuery({
    queryKey: ['faxina-itens', familia, DIAS],
    queryFn: () => faxinaItens({ familia, dias: DIAS, limite: 40 }),
  });

  if (itens.isPending) return <Carregando texto="Lendo os pedidos" />;
  if (itens.error) return <Aviso tipo="erro">{mensagemDeErro(itens.error)}</Aviso>;
  if (!itens.data || itens.data.length === 0) {
    return <Aviso tipo="ok">Nada pendente nesta família.</Aviso>;
  }

  return (
    <ul className="space-y-2">
      {itens.data.map((i) => (
        <li key={i.chave}><CartaoDoItem item={i} familia={familia} /></li>
      ))}
    </ul>
  );
}

/** O cartão de UM nome. Exportado para o teste poder desenhá-lo sozinho — o
 *  ambiente é node, sem jsdom: só o primeiro quadro, nenhum clique. */
export function CartaoDoItem({ item, familia }: {
  item: FaxinaItem;
  /** Para invalidar a lista certa depois de marcar. */
  familia?: string | null;
}) {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const parecido = avisoDeParecido(item);

  const marcar = useMutation({
    mutationFn: () => marcarFaxina({
      chave: item.chave, situacao: 'nao_e_peca', descricao: item.descricao,
    }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['faxina-itens', familia ?? null, DIAS] });
      cliente.invalidateQueries({ queryKey: ['faxina-familias', DIAS] });
    },
  });

  return (
    <div className="cartao space-y-2 p-3">
      <p className="font-medium">{rotuloDoItem(item)}</p>

      <p className="text-xs text-mute">{resumoDasFontes(item)}</p>

      <p className="flex flex-wrap items-center gap-2 text-xs text-mute">
        <span>{qtd(item.pecas)} peça(s)</span>
        {item.ultima && <span>última em {dataBR(item.ultima)}</span>}
        {item.carro && <span className="font-mono">{item.carro}</span>}
        {/* O código é de USO INTERNO (071): nunca sai em documento de cliente,
            mas é exatamente o que falta para o cadastro ficar completo. */}
        {item.codigos && item.codigos.length > 0 && (
          <span className="font-mono">{item.codigos.join(' / ')}</span>
        )}
      </p>

      {parecido && <p className="text-xs text-alerta">{parecido}</p>}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="botao-secundario min-h-11"
                onClick={() => navegar('/cadastro', {
                  state: {
                    descricao: item.descricao,
                    codigo: item.codigos?.[0] ?? '',
                    montadora: item.carro?.split(' ')[0] ?? '',
                    modelo: item.carro?.split(' ').slice(1).join(' ') ?? '',
                    chave: item.chave,
                  },
                })}>
          Cadastrar
        </button>
        {/* "ENTREGA" apareceu 7 vezes na lista como se fosse peça. Sem este
            botão ela volta toda semana e a lista perde a confiança de quem a
            usa — que é a única coisa que a faz funcionar. */}
        <button type="button" className="botao-terciario min-h-11"
                disabled={marcar.isPending}
                onClick={() => marcar.mutate()}
                aria-label={`Isto não é peça: ${rotuloDoItem(item)}`}>
          {marcar.isPending ? 'Tirando…' : 'Isto não é peça'}
        </button>
      </div>

      {marcar.error && <Aviso tipo="erro">{mensagemDeErro(marcar.error)}</Aviso>}
    </div>
  );
}
